import type { NavLink } from '@/hooks/useHeaderNavigation'
import { cn } from '@/lib/utils'
import { ChevronDown } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { NavSubmenuItem } from './NavSubmenuItem'

type Props = {
  readonly navLinks: NavLink[]
  readonly isLinkActive: (link: NavLink) => boolean
  readonly isSubLinkActive: (subLink: { path: string }) => boolean
  /** Userpilot anchor, set only while this nav is the visible one. */
  readonly userpilotAnchor?: string
}

export function DesktopNav({ navLinks, isLinkActive, isSubLinkActive, userpilotAnchor }: Props) {
  const { t } = useTranslation()
  const [desktopSubmenuOpen, setDesktopSubmenuOpen] = useState<string | null>(null)
  const desktopNavRef = useRef<HTMLElement>(null)
  const desktopTriggerRef = useRef<HTMLButtonElement | null>(null)

  // Close desktop submenu on click-outside or Escape key
  useEffect(() => {
    if (!desktopSubmenuOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      if (desktopNavRef.current && !desktopNavRef.current.contains(event.target as Node)) {
        setDesktopSubmenuOpen(null)
      }
    }

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        const triggerButton = desktopTriggerRef.current
        setDesktopSubmenuOpen(null)
        requestAnimationFrame(() => {
          triggerButton?.focus()
        })
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscapeKey)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [desktopSubmenuOpen])

  return (
    <nav ref={desktopNavRef} aria-label={t('header.mainNavigation')} className='mt-1' data-userpilot={userpilotAnchor}>
      <ul role='list' className='flex list-none items-center gap-8'>
        {navLinks.map((link) => {
          const isActive = isLinkActive(link)

          if (link.submenu) {
            const isSubmenuOpen = desktopSubmenuOpen === link.path

            return (
              <li key={link.path} className='group relative'>
                <button
                  type='button'
                  onClick={(e) => {
                    desktopTriggerRef.current = e.currentTarget
                    setDesktopSubmenuOpen(isSubmenuOpen ? null : link.path)
                  }}
                  aria-expanded={isSubmenuOpen}
                  className={cn(
                    'flex cursor-pointer items-center gap-1 border-b-3 text-base font-bold text-gray-700 transition-colors py-1',
                    isActive ? 'border-[#922fa4]' : 'border-transparent'
                  )}
                >
                  {link.label}
                  <ChevronDown
                    size={16}
                    aria-hidden='true'
                    className={cn('transition-transform', isSubmenuOpen && 'rotate-180')}
                  />
                </button>
                <div
                  aria-hidden={!isSubmenuOpen}
                  inert={!isSubmenuOpen ? true : undefined}
                  className={cn('absolute top-full left-0 z-50 pt-2', !isSubmenuOpen && 'hidden')}
                >
                  <ul
                    role='list'
                    aria-label={link.label}
                    className='grid list-none min-w-[400px] grid-cols-2 gap-2 rounded-lg border border-gray-700 bg-[#1a1f36] p-4 shadow-2xl'
                  >
                    {link.submenu.map((subLink) => (
                      <NavSubmenuItem
                        key={subLink.path}
                        path={subLink.path}
                        label={subLink.label}
                        description={subLink.description}
                        isActive={isSubLinkActive(subLink)}
                        variant='desktop'
                        onClick={() => setDesktopSubmenuOpen(null)}
                      />
                    ))}
                  </ul>
                </div>
              </li>
            )
          }

          return (
            <li key={link.path}>
              <Link
                to={link.path}
                aria-current={isActive ? 'page' : undefined}
                className={cn(
                  'flex items-center border-b-3 text-base font-bold text-gray-700 transition-colors py-1',
                  isActive ? 'border-[#922fa4]' : 'border-transparent'
                )}
              >
                {link.label}
              </Link>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
